import { Component, OnDestroy, OnInit } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { filter, Observable } from 'rxjs';
import { Entity, View } from '../../../../../shared/store/view/types';
import {
  KnownIpAddressFormModel,
  KnownIpAddressModel,
} from '../../../../../shared/store/attendance/known-ip-address/types';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { SubHeaderService } from '../../../../../shared/store/subheader/subheader.service';
import { BreadcrumbService } from 'xng-breadcrumb';
import { ViewService } from '../../../../../shared/store/view/views.service';
import { FormService } from '../../../../../shared/store/form/form.service';
import { KnownIpAddressService } from '../../../../../shared/store/attendance/known-ip-address/known-ip-address.service';
import { take } from 'rxjs/operators';

@UntilDestroy()
@Component({
  selector: 'app-known-ip-address-edit',
  standalone: false,
  templateUrl: './known-ip-address-edit.component.html',
  styleUrl: './known-ip-address-edit.component.scss',
})
export class KnownIpAddressEditComponent implements OnInit, OnDestroy {
  view$: Observable<View>;
  knownIpAddress$: Observable<KnownIpAddressModel>;
  knownIpAddressId: number;

  knownIpAddressForm: FormGroup;

  constructor(
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly subHeaderService: SubHeaderService,
    private readonly breadcrumbService: BreadcrumbService,
    private readonly viewService: ViewService,
    private readonly formBuilder: FormBuilder,
    private readonly formService: FormService,
    private readonly knownIpAddressService: KnownIpAddressService,
  ) {
    this.view$ = this.viewService.getViews.value$;
    this.knownIpAddress$ = this.knownIpAddressService.getById.value$;
  }

  ngOnInit(): void {
    this.knownIpAddressForm = this.formBuilder.group({
      address: ['', [Validators.required]],
      workLocationId: [null],
    });

    this.knownIpAddressId = this.route.snapshot.params['id'];
    this.knownIpAddressService.getById.call(this.knownIpAddressId);

    this.subHeaderService
      .getTriggerAction()
      .pipe(untilDestroyed(this))
      .subscribe((x) => {
        if (x == 'knownIpAddressUpdate') this.onSubmit();
        if (x == 'knownIpAddressDetails') {
          this.formService.clear();
          this.router.navigate([
            '/Configuration/KnownIpAddresses/Details',
            this.knownIpAddressId,
          ]);
        }
        this.subHeaderService.setTriggerAction('');
      });

    this.view$
      .pipe(
        untilDestroyed(this),
        filter((x) => !!x),
      )
      .subscribe((x) => {
        this.breadcrumbService.set('@list', x.title);
      });

    this.knownIpAddress$
      .pipe(
        filter((x) => !!x),
        take(1),
      )
      .subscribe((x) => {
        this.breadcrumbService.set('@edit', x.address);
        this.knownIpAddressForm.patchValue({
          address: x.address,
          workLocationId: x.workLocationId,
        });
      });
  }

  onSubmit() {
    if (this.knownIpAddressForm.invalid) {
      this.knownIpAddressForm.markAllAsTouched();
      this.formService.setEntityErrors('KNOWN_IP_ADDRESS');
      this.formService.countErrors(this.knownIpAddressForm, true);
      return;
    }

    const knownIpAddressFormModel: KnownIpAddressFormModel = {
      id: +this.knownIpAddressId,
      workLocationId: this.knownIpAddressForm.value.workLocationId!,
    };

    let result$ = this.knownIpAddressService.update.call(
      knownIpAddressFormModel,
    );

    result$
      .pipe(
        filter((x) => !!x),
        take(1),
      )
      .subscribe((result) => {
        this.formService.clear();
        this.router.navigate([
          '/Configuration/KnownIpAddresses/Details',
          result.id,
        ]);
      });
  }

  ngOnDestroy(): void {
    this.knownIpAddressService.getById.reset();
    this.knownIpAddressService.update.reset();
    this.breadcrumbService.set('@edit', ' ');
  }

  protected readonly Entity = Entity;
}
